// src/pages/finance-portal/fixed-assets/AssetDetail.js
//
// Read-only asset detail with embedded sub-panels: depreciation schedule,
// transfers, maintenance records, depreciation entries.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
    Grid, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Tabs, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useSnackbar } from 'notistack';

import axiosInstance from '../../../utils/axiosInstance';
import {
    FA_FONT, FA_ACCENT, FA_ACCENT_DARK, ASSET_STATUS_COLORS,
    formatCurrency, formatDate, statusChip, humanizeStatus,
} from './components/fixedAssetFormatters';

const headSx = {
    fontFamily: FA_FONT, fontWeight: 700, fontSize: '0.72rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};
const cellSx = { fontFamily: FA_FONT, fontSize: '0.84rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' };

function Field({ label, value }) {
    return (
        <Box>
            <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.92rem', color: '#0f172a', mt: 0.25 }}>
                {value ?? '\u2014'}
            </Typography>
        </Box>
    );
}

export default function AssetDetail() {
    const { assetId } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [asset, setAsset] = useState(null);
    const [schedule, setSchedule] = useState(null);
    const [bookType, setBookType] = useState('BOOK');
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAsset = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/fixed-assets/assets/${assetId}`);
            if (res.data?.success) setAsset(res.data.data);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load asset');
        } finally {
            setLoading(false);
        }
    }, [assetId]);

    const fetchSchedule = useCallback(async () => {
        try {
            const res = await axiosInstance.get(
                `/api/finance/fixed-assets/assets/${assetId}/schedule`,
                { params: { book_type: bookType } });
            if (res.data?.success) setSchedule(res.data.data);
        } catch (e) {
            /* silent */
        }
    }, [assetId, bookType]);

    useEffect(() => { fetchAsset(); }, [fetchAsset]);
    useEffect(() => { if (tab === 1) fetchSchedule(); }, [tab, fetchSchedule]);

    if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: FA_ACCENT }} /></Box>;
    if (!asset) return <Box sx={{ p: 4 }}><Alert severity="error">Asset not found</Alert></Box>;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: `linear-gradient(135deg, ${FA_ACCENT} 0%, ${FA_ACCENT_DARK} 100%)`,
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/finance-portal/fixed-assets/assets')}
                            sx={{ color: 'white', textTransform: 'none', fontFamily: FA_FONT }}>Back</Button>
                        <Box>
                            <Typography sx={{ fontFamily: FA_FONT, fontWeight: 800, color: 'white', fontSize: '1.5rem' }}>
                                {asset.name}
                            </Typography>
                            <Typography sx={{ fontFamily: FA_FONT, color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
                                #{asset.asset_number}
                                {asset.tag_number ? ` · Tag ${asset.tag_number}` : ''}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip label={humanizeStatus(asset.status)}
                            sx={{ fontFamily: FA_FONT, fontWeight: 700, ...statusChip(asset.status, ASSET_STATUS_COLORS) }} />
                        <Button onClick={() => navigate(`/finance-portal/fixed-assets/assets/${assetId}/edit`)}
                            startIcon={<EditIcon />} variant="contained"
                            sx={{ fontFamily: FA_FONT, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: FA_ACCENT, '&:hover': { bgcolor: 'rgba(255,255,255,0.92)' } }}>
                            Edit
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1300, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, fontFamily: FA_FONT }}>{error}</Alert>}

                {/* KPI strip */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[
                        { label: 'Cost Basis', value: formatCurrency(asset.total_capitalized_cost) },
                        { label: 'Accumulated Depreciation', value: formatCurrency(asset.book_accumulated_depreciation) },
                        { label: 'Impairment', value: formatCurrency(asset.impairment_loss) },
                        { label: 'Book Value', value: formatCurrency(asset.book_value) },
                    ].map((card, i) => (
                        <Grid item xs={6} md={3} key={i}>
                            <Card sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>
                                        {card.label}
                                    </Typography>
                                    <Typography sx={{ fontFamily: FA_FONT, fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', mt: 0.25 }}>
                                        {card.value}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e2e8f0', px: 2 }}
                        TabIndicatorProps={{ style: { backgroundColor: FA_ACCENT } }}>
                        <Tab label="Overview" sx={{ fontFamily: FA_FONT, textTransform: 'none', fontWeight: 600 }} />
                        <Tab label="Depreciation Schedule" sx={{ fontFamily: FA_FONT, textTransform: 'none', fontWeight: 600 }} />
                        <Tab label="Posted Entries" sx={{ fontFamily: FA_FONT, textTransform: 'none', fontWeight: 600 }} />
                        <Tab label="Transfers" sx={{ fontFamily: FA_FONT, textTransform: 'none', fontWeight: 600 }} />
                        <Tab label="Maintenance" sx={{ fontFamily: FA_FONT, textTransform: 'none', fontWeight: 600 }} />
                    </Tabs>

                    <CardContent sx={{ p: 3 }}>
                        {tab === 0 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}><Field label="Category" value={asset.category_name} /></Grid>
                                <Grid item xs={12} md={6}><Field label="Asset Class" value={asset.asset_class && humanizeStatus(asset.asset_class)} /></Grid>
                                <Grid item xs={12} md={6}><Field label="Acquisition Date" value={formatDate(asset.acquisition_date)} /></Grid>
                                <Grid item xs={12} md={6}><Field label="In Service Date" value={formatDate(asset.in_service_date)} /></Grid>
                                <Grid item xs={12} md={6}><Field label="Useful Life" value={`${asset.useful_life_months || 0} months`} /></Grid>
                                <Grid item xs={12} md={6}><Field label="Method" value={`${asset.book_method} (${asset.book_convention})`} /></Grid>
                                <Grid item xs={12} md={6}><Field label="Tax Method" value={asset.tax_method} /></Grid>
                                <Grid item xs={12} md={6}><Field label="Section 179" value={formatCurrency(asset.section_179_deduction)} /></Grid>
                                <Grid item xs={12} md={6}><Field label="Bonus Depreciation %" value={`${asset.bonus_depreciation_pct || 0}%`} /></Grid>
                                <Grid item xs={12} md={6}><Field label="Location / Department" value={`${asset.location || '\u2014'} / ${asset.department || '\u2014'}`} /></Grid>
                                <Grid item xs={12} md={6}><Field label="Insurance Value" value={asset.insurance_value != null ? formatCurrency(asset.insurance_value) : '\u2014'} /></Grid>
                                <Grid item xs={12} md={6}><Field label="Replacement Cost" value={asset.replacement_cost != null ? formatCurrency(asset.replacement_cost) : '\u2014'} /></Grid>
                                <Grid item xs={12}><Field label="Description" value={asset.description} /></Grid>
                                <Grid item xs={12}><Field label="Notes" value={asset.notes} /></Grid>
                            </Grid>
                        )}

                        {tab === 1 && (
                            <Box>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                                    <Typography sx={{ fontFamily: FA_FONT, color: '#64748b', fontSize: '0.85rem' }}>Book:</Typography>
                                    {['BOOK', 'TAX'].map(bt => (
                                        <Chip key={bt} label={bt}
                                            onClick={() => setBookType(bt)}
                                            sx={{ fontFamily: FA_FONT, cursor: 'pointer',
                                                bgcolor: bookType === bt ? FA_ACCENT : '#f1f5f9',
                                                color: bookType === bt ? '#fff' : '#475569',
                                                fontWeight: 600 }} />
                                    ))}
                                </Box>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                {['Period', 'Opening BV', 'Depreciation', 'Accumulated', 'Closing BV'].map(h => (
                                                    <TableCell key={h} sx={headSx}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {!schedule || (schedule.periods || []).length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                                        <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>
                                                            Place asset in service to generate a schedule.
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                schedule.periods.map((p, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell sx={cellSx}>{p.period_label}</TableCell>
                                                        <TableCell sx={cellSx}>{formatCurrency(p.opening_book_value)}</TableCell>
                                                        <TableCell sx={cellSx}>{formatCurrency(p.depreciation_amount)}</TableCell>
                                                        <TableCell sx={cellSx}>{formatCurrency(p.accumulated_depreciation)}</TableCell>
                                                        <TableCell sx={cellSx}>{formatCurrency(p.closing_book_value)}</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        {tab === 2 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {['Period', 'Book', 'Amount', 'Accumulated After', 'Status', 'JE'].map(h => (
                                                <TableCell key={h} sx={headSx}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(asset.depreciation_entries || []).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>
                                                        No depreciation has been posted yet.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : asset.depreciation_entries.map(e => (
                                            <TableRow key={e.id}>
                                                <TableCell sx={cellSx}>{e.period_label}</TableCell>
                                                <TableCell sx={cellSx}>{e.book_type}</TableCell>
                                                <TableCell sx={cellSx}>{formatCurrency(e.depreciation_amount)}</TableCell>
                                                <TableCell sx={cellSx}>{formatCurrency(e.accumulated_after)}</TableCell>
                                                <TableCell sx={cellSx}>{humanizeStatus(e.status)}</TableCell>
                                                <TableCell sx={cellSx}>{e.journal_entry_id || '\u2014'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {tab === 3 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {['Date', 'Number', 'From', 'To', 'Reason', 'Status'].map(h => (
                                                <TableCell key={h} sx={headSx}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(asset.transfers || []).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>No transfers</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : asset.transfers.map(t => (
                                            <TableRow key={t.id}>
                                                <TableCell sx={cellSx}>{formatDate(t.transfer_date)}</TableCell>
                                                <TableCell sx={cellSx}>{t.transfer_number}</TableCell>
                                                <TableCell sx={cellSx}>{t.from_location || t.from_department || '\u2014'}</TableCell>
                                                <TableCell sx={cellSx}>{t.to_location || t.to_department || '\u2014'}</TableCell>
                                                <TableCell sx={cellSx}>{humanizeStatus(t.reason)}</TableCell>
                                                <TableCell sx={cellSx}>{humanizeStatus(t.status)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {tab === 4 && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {['Date', 'Type', 'Description', 'Cost', 'Treatment', 'Status'].map(h => (
                                                <TableCell key={h} sx={headSx}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(asset.maintenance_records || []).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                                    <Typography sx={{ fontFamily: FA_FONT, color: '#94a3b8' }}>No maintenance records</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : asset.maintenance_records.map(m => (
                                            <TableRow key={m.id}>
                                                <TableCell sx={cellSx}>{formatDate(m.maintenance_date)}</TableCell>
                                                <TableCell sx={cellSx}>{humanizeStatus(m.maintenance_type)}</TableCell>
                                                <TableCell sx={cellSx}>{m.description || '\u2014'}</TableCell>
                                                <TableCell sx={cellSx}>{formatCurrency(m.cost)}</TableCell>
                                                <TableCell sx={cellSx}>{humanizeStatus(m.treatment)}</TableCell>
                                                <TableCell sx={cellSx}>{humanizeStatus(m.status)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}
